import { useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import I18n from 'i18n-js';

import { logout } from '@lootboy/lib/actions/AuthActions';
import { approveLegalData } from '@lootboy/lib/actions/UserActions';
import { userNeedsEmailVerificationSelector } from '@lootboy/lib/selectors';

import Alert from '~/components/Alert';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';

import { LegalPopupTemplate } from '~/popups/LegalPopups/LegalPopupTemplate';
import { legalDataSelectorWithInvalidate } from '~/redux/selectors';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  EMAIL_REGISTER_PATH,
  REQUEST_EMAIL_VERIFICATION_PATH,
  USER_DETAILS_PATH,
} from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';

import styles from './TermsConsentPopup.module.scss';

type TTermsPopupProps = {
  onClose?: () => void;
  onApprove?: () => void;
  wasLegalDataUpdated?: boolean;
};

/**
 * This is used as a standalone screen without any props in the registration flow, and as a GDPR dialog
 * is case the legal data has updated or user is on a new browser/device
 */
export function TermsConsentPopup({
  onApprove,
  onClose,
  wasLegalDataUpdated = false,
}: TTermsPopupProps): JSX.Element {
  const acceptingRef = useRef(false);
  const alert = useAlert();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { privacy } = useSelector(legalDataSelectorWithInvalidate);
  // if the user has registered through a social provider they don't need to verify their email
  const userNeedEmailVerification = useSelector<TAppStore, boolean>(
    userNeedsEmailVerificationSelector,
  );
  const [processing, setProcessing] = useState(false);

  const acceptTermsOfService = async () => {
    try {
      acceptingRef.current = true;
      setProcessing(true);
      await dispatch(approveLegalData());
      if (onApprove) {
        onApprove();
      } else if (userNeedEmailVerification) {
        history.push(REQUEST_EMAIL_VERIFICATION_PATH);
      } else {
        history.push(USER_DETAILS_PATH);
      }
    } catch (error) {
      acceptingRef.current = false;
      setProcessing(false);
      const { message } = parseLootBoyError(error);
      alert.error(
        <Alert
          title={I18n.t('common_error')}
          body={message}
        />,
      );
    }
  };

  return (
    <>
      <LegalPopupTemplate
        type={wasLegalDataUpdated ? 'termsUpdate' : 'terms'}
        onClose={() => {
          if (!acceptingRef.current) {
            if (onClose) {
              onClose();
            } else {
              dispatch(logout());
              history.push(EMAIL_REGISTER_PATH);
            }
          }
        }}
      >
        {privacy}
      </LegalPopupTemplate>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          disabled={processing}
          onClick={acceptTermsOfService}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </>
  );
}
